import { InPlaceContainer }  from './InPlaceContainer.js';
import { InTargetContainer } from './InTargetContainer.js';
import { ContentLockContainer } from './ContentLockContainer.js';

/**
 * Container type identifiers.
 *
 * These mirror the Rust ContainerType enum serialised via
 * `#[serde(tag = "type", rename_all = "snake_case")]`.
 * The wire value of the `type` field must match exactly — change here and in Rust in tandem.
 *
 * IN_PLACE  — renders inline at the position of the bootstrap script tag or outer iframe.
 *             Default container type. No configuration required.
 *
 * IN_TARGET — renders at a specific DOM element resolved via a CSS selector.
 *             Requires config: { selector: string }
 */
export const CONTAINER_TYPE_IN_PLACE  = 'in_place';
export const CONTAINER_TYPE_IN_TARGET = 'in_target';
export const CONTAINER_TYPE_CONTENT_LOCK = 'content_lock';

/**
 * Registry and factory for container instances.
 *
 * getInstance constructs the appropriate container class for the given type,
 * with renderEnv and config baked in. The returned container is ready to call
 * isSupported(), applyStyles(), and renderLocation on.
 *
 * Throws on unknown container types — caught by the handlePlacement error boundary.
 */
export class ContainerManager {
  /**
   * @param {string} type         - container type identifier (CONTAINER_TYPE_* constant)
   * @param {object | undefined} config    - container config from the ad response (may be absent for InPlace)
   * @param {object} renderEnv    - captured render environment from getRenderEnv()
   * @returns {InPlaceContainer | InTargetContainer}
   * @throws {Error} if type is not a recognised container type
   */
  getInstance(type, config, renderEnv) {
    switch (type) {
      case CONTAINER_TYPE_IN_PLACE:
        return new InPlaceContainer(config ?? {}, renderEnv);

      case CONTAINER_TYPE_IN_TARGET:
        return new InTargetContainer(config, renderEnv);

      case CONTAINER_TYPE_CONTENT_LOCK:
        return new ContentLockContainer(config, renderEnv);

      default:
        throw new Error(`Unknown container type '${type}'`);
    }
  }
}
